import { useEffect } from 'react';

interface PageMetaProps {
  title: string;
  description: string;
  keywords?: string;
  canonicalUrl?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogType?: string;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
}

export default function PageMeta({
  title,
  description,
  keywords,
  canonicalUrl,
  ogTitle,
  ogDescription,
  ogType = 'website',
  jsonLd,
}: PageMetaProps) {
  useEffect(() => {
    document.title = title;

    const setMetaTag = (name: string, content: string, attr: 'name' | 'property' = 'name') => {
      let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMetaTag('description', description);
    if (keywords) setMetaTag('keywords', keywords);

    if (canonicalUrl) {
      let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
      if (!link) {
        link = document.createElement('link');
        link.setAttribute('rel', 'canonical');
        document.head.appendChild(link);
      }
      link.setAttribute('href', canonicalUrl);
    }

    if (ogTitle || title) setMetaTag('og:title', ogTitle || title, 'property');
    if (ogDescription || description) setMetaTag('og:description', ogDescription || description, 'property');
    setMetaTag('og:type', ogType, 'property');
    if (canonicalUrl) setMetaTag('og:url', canonicalUrl, 'property');

    setMetaTag('twitter:card', 'summary_large_image', 'name');
    if (ogTitle || title) setMetaTag('twitter:title', ogTitle || title, 'name');
    if (ogDescription || description) setMetaTag('twitter:description', ogDescription || description, 'name');

    const scripts: HTMLScriptElement[] = [];
    if (jsonLd) {
      const existingScripts = document.querySelectorAll('script[type="application/ld+json"][data-dynamic="true"]');
      existingScripts.forEach((s) => s.remove());

      const items = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
      items.forEach((item) => {
        const scriptEl = document.createElement('script');
        scriptEl.type = 'application/ld+json';
        scriptEl.setAttribute('data-dynamic', 'true');
        scriptEl.textContent = JSON.stringify(item);
        document.head.appendChild(scriptEl);
        scripts.push(scriptEl);
      });
    }

    return () => {
      scripts.forEach((s) => s.remove());
    };
  }, [title, description, keywords, canonicalUrl, ogTitle, ogDescription, ogType, jsonLd]);

  return null;
}